import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import { closePlan, listPlans, reopenPlan, type RadGroup } from "../../api/catalog";
import { PlanDetails } from "./PlanDetails";
import { loadBilling, planPriceCell } from "../reports/planPrices";

/**
 * Plans › On sale (owner 2026-10-11, design §9.16). Close a plan to new
 * sales, or reopen it. Closing never touches a price: subscribers already on
 * the plan keep renewing it at its old price until they are given a new
 * offer; the shop (portal and PHP) stops offering it to anyone else.
 */
export function PlansOnSale() {
  const identity = useIdentity();
  const own = identity.namespace ?? "";
  const [target, setTarget] = useState(own);
  const [ns, setNs] = useState(own);
  const [asking, setAsking] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const qc = useQueryClient();

  const plans = useQuery({
    queryKey: ["plans-on-sale", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await listPlans(identity, ns)).data ?? [],
  });
  // Owner 2026-10-29: the price is read from billing, never from the description.
  const billing = useQuery({
    queryKey: ["plans-on-sale-prices", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => loadBilling(identity, [ns], signal),
  });

  const act = useMutation<unknown, Error, { g: RadGroup; close: boolean }>({
    mutationFn: ({ g, close }) => (close ? closePlan : reopenPlan)(identity, ns, g.groupname),
    onSuccess: () => {
      setAsking(null);
      void qc.invalidateQueries({ queryKey: ["plans-on-sale", ns] });
    },
  });
  const notYet = act.error instanceof ApiError && (act.error.status === 404 || act.error.status === 405);

  const rows = [...(plans.data ?? [])].sort(
    (a, b) => Number(b.status) - Number(a.status) || (a.group_hd || a.groupname).localeCompare(b.group_hd || b.groupname),
  );

  return (
    <Box sx={{ p: 2, maxWidth: 1000 }}>
      <Typography variant="h5" gutterBottom>
        Plans on sale
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Close a plan to new sales when you stop offering it. Subscribers already on it keep renewing it at their price
        until you give them a new offer; nobody else can buy it. A closed plan can be reopened. Nothing is deleted.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" label="Namespace" value={target} sx={{ minWidth: 320 }}
          onChange={(e) => setTarget(e.target.value.trim().toLowerCase())} />
        <Button variant="outlined" sx={{ height: 40 }} onClick={() => setNs(target)}>
          Open
        </Button>
      </Stack>

      {plans.isLoading && <Typography variant="body2">Loading plans…</Typography>}
      {plans.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Could not load the plans</AlertTitle>
          {(plans.error as Error).message}
        </Alert>
      )}
      {act.isError && (
        <Alert severity={notYet ? "info" : "error"} sx={{ mb: 2 }}>
          <AlertTitle>{notYet ? "Reopening is not available yet" : "Not changed"}</AlertTitle>
          {notYet ? "Reopening a plan arrives with the next gateway release." : act.error.message}
        </Alert>
      )}
      {plans.data && rows.length === 0 && <Typography variant="body2">No plans in {ns}.</Typography>}
      {rows.length > 0 && (
        <Paper>
          <Table size="small" aria-label="Plans on sale">
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((g) => (
                <Fragment key={g.groupname}>
                <TableRow>
                  <TableCell>{g.group_hd || g.groupname}</TableCell>
                  <TableCell>
                    <code>{g.groupname}</code>
                  </TableCell>
                  <TableCell>{planPriceCell({ ...g, namespace: ns }, billing.data)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={g.status ? "On sale" : "Not on sale"} color={g.status ? "success" : "default"} />
                  </TableCell>
                  <TableCell align="right">
                    {asking === g.groupname ? (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="contained" color="warning" disabled={act.isPending}
                          onClick={() => act.mutate({ g, close: g.status })}>
                          {g.status ? "Yes, close it" : "Yes, reopen it"}
                        </Button>
                        <Button size="small" onClick={() => setAsking(null)}>
                          Cancel
                        </Button>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => setOpened(opened === g.groupname ? null : g.groupname)}>
                          {opened === g.groupname ? "Hide" : "Details"}
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => { act.reset(); setAsking(g.groupname); }}>
                          {g.status ? "Close to new sales" : "Reopen"}
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
                {opened === g.groupname && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <PlanDetails ns={ns} group={g} />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
